import request from '@teetsh/app/src/utils/request';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { format } from '@teetsh/app/src/utils/time/format';
import { TFunction } from 'i18next';
import useNotification from 'containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';
import { UserLocale } from 'types/user';
import { AppelsStatsPDFSettings } from 'containers/RegistreAppel/Print/types';
import { buildAppelsStatsPDFParams } from 'containers/RegistreAppel/Preview/Stats/Sidebar/buildAppelsStatsPDFParams';

interface Props {
  schoolyearId: string;
  schoolId: string;
  eleveId?: string;
  onStart: () => void;
  onFinished: () => void;
  onError: () => void;
  from: Date;
  to: Date;
  lng: UserLocale;
  tz: string;
  t: TFunction<['common', 'registreAppel']>;
  settings: AppelsStatsPDFSettings;
}

function fetchAppelsStats(s3Url: string, pyramideName: string) {
  return fetch(s3Url)
    .then(response => response.blob())
    .then(response => {
      saveAs(response, `${pyramideName}.pdf`);
    });
}

export async function downloadAppelsStats(props: Props) {
  const {
    schoolyearId,
    schoolId,
    onStart,
    onFinished,
    onError,
    from,
    to,
    eleveId,
    lng,
    tz,
    t,
    settings,
  } = props;

  onStart();

  const params = buildAppelsStatsPDFParams({
    from,
    to,
    eleveId,
    lng,
    tz,
    settings,
  });
  const requestURL = `${
    import.meta.env.VITE_API_URL
  }/schoolyears/${schoolyearId}/schools/${schoolId}/appels/stats/generate?${params.toString()}`;
  try {
    const response = await request(requestURL, {
      method: 'GET',
    });
    const token = response.pdf_token;
    if ((window as any)?._isPlaywright) {
      (window as any).appelPrintedUrl = response?.printUrl;
    }

    await fetchAppelsStats(
      token,
      t('registreAppel:registreFileName', {
        from: format(from, lng, t('common:datePattern.dd-MM-yyyy')),
        to: format(to, lng, t('common:datePattern.dd-MM-yyyy')),
      }),
    );
  } catch {
    onError();
  } finally {
    onFinished();
  }
}

export function useDownloadAppelsStats() {
  const { schoolyear, school } = useSchoolSchoolyear();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { notifyError } = useNotification();
  const { t } = useTranslation(['common', 'registreAppel']);

  const download = ({
    from,
    to,
    eleveId,
    lng,
    tz,
    settings,
  }: {
    from: Date;
    to: Date;
    eleveId?: string;
    lng: UserLocale;
    tz: string;
    settings: AppelsStatsPDFSettings;
  }) =>
    downloadAppelsStats({
      from,
      to,
      lng,
      tz,
      t,
      settings,
      schoolyearId: schoolyear.id,
      schoolId: school.id,
      onStart: () => setIsDownloading(true),
      onFinished: () => setIsDownloading(false),
      onError: () => notifyError({ title: t('common:messages.genericError') }),
      eleveId,
    });

  return {
    download,
    isDownloading,
  };
}
